import React, { useState, useEffect, useCallback, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Pagination from "../Pagination/pagination";
import Preloader from "../preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import {
  toDisplayMediaUrl,
  pathsMatchForSelection,
  uploadCompanyMediaFilesBulk,
  validateImageFile,
  MAX_IMAGE_SIZE_LABEL,
} from "../../../utils/mediaImageUtils";

const ProductMediaGallery = ({
  compID,
  mediaCategory = "Product",
  onSelectImage,
  selectedPath,
  autoSeedOnEmpty = true,
  variant = "modal",
  multipleUpload = false,
  enableSelection,
}) => {
  const isPageVariant = variant === "page";
  const selectionEnabled = enableSelection ?? !!onSelectImage;
  const [activeTab, setActiveTab] = useState("gallery");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [seedAttempted, setSeedAttempted] = useState(false);
  const fileInputRef = useRef(null);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchMedia = useCallback(async () => {
    if (!compID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_MEDIA, {
        params: {
          CompID: compID,
          MediaCategory: mediaCategory,
          Search: debouncedSearch || undefined,
          PageNumber: pageNumber,
          PageSize: pageSize,
        },
      });
      const payload = response.data?.data || response.data;
      const list = payload?.items || payload?.Items || [];
      setItems(Array.isArray(list) ? list : []);
      setTotalRecords(payload?.totalRecords ?? payload?.TotalRecords ?? 0);
      setTotalPages(payload?.totalPages ?? payload?.TotalPages ?? 1);
    } catch (err) {
      showErrorToast("Failed to load media gallery");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [compID, mediaCategory, debouncedSearch, pageNumber, pageSize]);

  const seedFromProducts = useCallback(async () => {
    if (!compID || seedAttempted) return;
    try {
      setSeedAttempted(true);
      await axiosInstance.post(APIs.POST_SEED_COMPANY_MEDIA, null, {
        params: { CompID: compID },
      });
    } catch {
      // Non-blocking; gallery may still have uploads
    }
  }, [compID, seedAttempted]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, mediaCategory]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  useEffect(() => {
    if (
      autoSeedOnEmpty &&
      !loading &&
      !seedAttempted &&
      items.length === 0 &&
      !debouncedSearch &&
      pageNumber === 1 &&
      compID
    ) {
      seedFromProducts().then(() => fetchMedia());
    }
  }, [
    autoSeedOnEmpty,
    loading,
    seedAttempted,
    items.length,
    debouncedSearch,
    pageNumber,
    compID,
    seedFromProducts,
    fetchMedia,
  ]);

  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("ImageFile", file);
    const response = await axiosInstance.post(APIs.POST_UPLOAD_COMPANY_MEDIA, formData, {
      params: { CompID: compID, MediaCategory: mediaCategory },
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (response.data?.status !== true) {
      throw new Error(response.data?.message || "Upload failed");
    }
    return response.data?.data || {};
  };

  const handleFilesUpload = async (fileList) => {
    if (!compID) return;
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = [];
    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        showErrorToast(validation.message || `${file.name}: invalid image`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    try {
      setUploading(true);

      if (multipleUpload) {
        setUploadProgress({ current: 0, total: validFiles.length });
        const result = await uploadCompanyMediaFilesBulk(
          axiosInstance,
          APIs,
          compID,
          validFiles,
          mediaCategory
        );
        const uploaded = result.uploaded || result.Uploaded || [];
        const failed = result.failed || result.Failed || [];
        const successCount = result.successCount ?? result.SuccessCount ?? uploaded.length;

        failed.forEach((item) => {
          const name = item.originalFileName || item.OriginalFileName || "File";
          const message = item.message || item.Message || "upload failed";
          showErrorToast(`${name}: ${message}`);
        });

        if (successCount > 0) {
          showSuccessToast(
            successCount === 1 ? "Image uploaded" : `${successCount} images uploaded`
          );
          setActiveTab("gallery");
          await fetchMedia();
        }
        return;
      }

      let successCount = 0;
      let lastUploaded = null;

      for (let i = 0; i < validFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: validFiles.length });
        try {
          const data = await uploadSingleFile(validFiles[i]);
          successCount += 1;
          const filePath = data.filePath || data.FilePath;
          const fullUrl = data.fullUrl || data.FullUrl || toDisplayMediaUrl(filePath);
          lastUploaded = { fullUrl, filePath };
        } catch (err) {
          showErrorToast(`${validFiles[i].name}: ${err.message || "upload failed"}`);
        }
      }

      if (successCount > 0) {
        showSuccessToast(
          successCount === 1 ? "Image uploaded" : `${successCount} images uploaded`
        );
        setActiveTab("gallery");
        await fetchMedia();
        if (onSelectImage && lastUploaded && !multipleUpload) {
          onSelectImage(lastUploaded.fullUrl, lastUploaded.filePath, null);
        }
      }
    } catch (err) {
      showErrorToast(err.message || "Failed to upload images");
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpload = (e) => {
    const files = multipleUpload ? e.target.files : e.target.files?.[0] ? [e.target.files[0]] : [];
    handleFilesUpload(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (uploading) return;
    handleFilesUpload(e.dataTransfer?.files);
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm("Delete this image permanently? This cannot be undone.")) return;
    try {
      await axiosInstance.delete(APIs.DELETE_COMPANY_MEDIA, {
        params: { CompID: compID, Media_ID: item.media_ID ?? item.Media_ID },
      });
      showSuccessToast("Image deleted");
      fetchMedia();
    } catch {
      showErrorToast("Failed to delete image");
    }
  };

  const tabStyle = (tab) => ({
    border: "none",
    background: activeTab === tab ? themeColor : isDarkMode ? "#3c4043" : "#e9ecef",
    color: activeTab === tab ? "#fff" : isDarkMode ? "#fff" : "#333",
    padding: "8px 20px",
    borderRadius: "20px",
    fontWeight: 600,
    marginRight: "8px",
  });

  const renderUploadZone = (compact = false) => (
    <div
      className={`d-flex flex-column align-items-center justify-content-center rounded ${compact ? "p-5" : "p-4 mb-4"}`}
      style={{
        minHeight: compact ? "280px" : "160px",
        border: `2px dashed ${isDragOver ? themeColor : `${themeColor}60`}`,
        backgroundColor: isDragOver
          ? `${themeColor}15`
          : isDarkMode
            ? "#2d3436"
            : "#f8f9fa",
        cursor: uploading ? "default" : "pointer",
        transition: "border-color 0.2s, background-color 0.2s",
      }}
      onClick={() => !uploading && fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!uploading) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {uploading ? (
        <div className="text-center">
          <Preloader />
          {uploadProgress && (
            <p className="mb-0 mt-2 text-muted">
              Uploading {uploadProgress.current} of {uploadProgress.total}...
            </p>
          )}
        </div>
      ) : (
        <>
          <i
            className="fa-solid fa-cloud-arrow-up mb-2"
            style={{ fontSize: compact ? "2.5rem" : "2rem", color: themeColor }}
          />
          <p className="mb-1 fw-semibold">
            {multipleUpload ? "Click or drag images here" : "Click to upload an image"}
          </p>
          <small className="text-muted">
            {multipleUpload
              ? `Select multiple files — JPG, PNG, GIF, WebP — max ${MAX_IMAGE_SIZE_LABEL} each`
              : `JPG, PNG, GIF, WebP — max ${MAX_IMAGE_SIZE_LABEL}`}
          </small>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multipleUpload}
        className="d-none"
        onChange={handleUpload}
      />
    </div>
  );

  const renderGalleryGrid = () => (
    <>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by file name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            borderRadius: "8px",
            backgroundColor: isDarkMode ? "#2d3436" : "#fff",
            color: isDarkMode ? "#fff" : "#000",
            border: `1px solid ${themeColor}30`,
          }}
        />
      </div>

      {(loading || uploading) && !uploadProgress && <Preloader />}

      <div
        className="row g-2"
        style={
          isPageVariant
            ? { minHeight: "120px" }
            : { maxHeight: "420px", overflowY: "auto", minHeight: "120px" }
        }
      >
        {items.map((item) => {
          const filePath = item.filePath || item.FilePath || "";
          const imageUrl = item.imageUrl || item.ImageUrl || toDisplayMediaUrl(filePath);
          const isSelected =
            selectionEnabled &&
            (pathsMatchForSelection(selectedPath, filePath) ||
              pathsMatchForSelection(selectedPath, imageUrl));
          return (
            <div key={item.media_ID ?? item.Media_ID} className="col-6 col-md-3 col-lg-2">
              <div
                className="position-relative rounded overflow-hidden shadow-sm"
                style={{
                  cursor: selectionEnabled ? "pointer" : "default",
                  border: isSelected ? `3px solid ${themeColor}` : "1px solid #dee2e6",
                  aspectRatio: "1",
                  backgroundColor: isDarkMode ? "#404040" : "#f8f9fa",
                }}
                onClick={() => selectionEnabled && onSelectImage?.(imageUrl, filePath, null)}
                title={item.originalFileName || item.OriginalFileName || filePath}
              >
                <LazyLoadImage
                  src={imageUrl}
                  alt=""
                  effect="blur"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(ev) => {
                    ev.target.src = "/assets/img/icons/placeholder.svg";
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm position-absolute top-0 end-0 m-1 p-0 d-flex align-items-center justify-content-center"
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.9)",
                  }}
                  onClick={(e) => handleDelete(e, item)}
                  title="Delete permanently"
                >
                  <i className="fa fa-trash text-danger" style={{ fontSize: "10px" }} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && items.length === 0 && (
        <div className="text-center py-4 text-muted">
          <i className="fa fa-images fa-2x mb-2" />
          <p className="mb-0">No images yet. Upload one or add products with images.</p>
        </div>
      )}

      {totalRecords > 0 && (
        <div className="mt-3">
          <Pagination
            totalRecords={totalRecords}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalPages={totalPages}
            setPageNumber={setPageNumber}
            setPageSize={() => {}}
          />
        </div>
      )}
    </>
  );

  if (isPageVariant) {
    return (
      <div>
        {renderUploadZone(false)}
        {renderGalleryGrid()}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
        <button type="button" style={tabStyle("gallery")} onClick={() => setActiveTab("gallery")}>
          Gallery
        </button>
        <button type="button" style={tabStyle("upload")} onClick={() => setActiveTab("upload")}>
          Upload new
        </button>
      </div>

      {activeTab === "gallery" && renderGalleryGrid()}

      {activeTab === "upload" && renderUploadZone(true)}
    </div>
  );
};

export default ProductMediaGallery;
